import {
    EuiButton,
    EuiButtonIcon,
    EuiFlexGroup,
    EuiPagination,
    EuiTextAlign,
    EuiCheckbox,
} from "@elastic/eui";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { useHistory } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import AbstractDropdown from "../../../shared/entity/components/AbstractFormField/fields/AbstractDropdown";
import AbstractDate from "../../../shared/entity/components/AbstractFormField/fields/AbstractDate";
import AbstractNumber from "../../../shared/entity/components/AbstractFormField/fields/AbstractNumber";
import orderFormFields, { supplierPartnerContractTypeData } from "../../configs/createFormFields";

import {
    isTripValid,
    isOrderValid,
    mapOrderToEntityRequest,
    isTripPointsValid,
    getTripIds,
} from "../../utils";

import * as S from "../../style";
import TripForm from "./TripForm";
import { postOrder, getBranchLocation } from "../../api";
import OrderColumn from "./OrderColumn";
import AbstractFormContainer from "../../../shared/entity/components/AbstractForm/container";
import { extractResourceAttributes, generateEntityLabel } from "../../../../utils/jsonApiUtils";
import { setMessage } from "../../../shared/notification/slice";
import Api from "../../../../lib/api";
import trackAnalytics from "../../../../trackAnalytics";
import { getUserRoles } from "modules/auth/slice";
import { userRoles } from "constants/userRoles";

const tripPointModel = (tripPoint) => ({
    city: {
        id: tripPoint && tripPoint.city && tripPoint.city.id,
        name: tripPoint && tripPoint.city && tripPoint.city.name,
    },
    location: {
        id: tripPoint && tripPoint.location && tripPoint.location.id,
        name: tripPoint && tripPoint.location && tripPoint.location.name,
    },
});

export const tripFormModel = (currentTrip) => ({
    supplier: (currentTrip && currentTrip.supplier) || null,
    driver: (currentTrip && currentTrip.driver) || null,
    truck: (currentTrip && currentTrip.truck) || null,
    truckType: (currentTrip && currentTrip.truckType) || null,
    returnable: (currentTrip && currentTrip.returnable) || false,
    pickupDateAndTime: (currentTrip && currentTrip.pickupDateAndTime) || "",
    tripPoints: [],
    tripAttachments: [],
    tripNotes: (currentTrip && currentTrip.tripNotes) || "",
    tripType: (currentTrip && currentTrip.tripType) || null,
    // for UI state
    isValid: (currentTrip && currentTrip.isValid) || null,
    isPointsValid: (currentTrip && currentTrip.isPointsValid) || null,
});

export default function ({ isBulk }) {
    const [orderOrganization, setOrderOrganization] = useState(null);
    const [orderBranch, setOrderBranch] = useState(null);
    const [contractType, setContractType] = useState(null);
    const [branchLocation, setBranchLocation] = useState(null);
    const [orderSupplier, setOrderSupplier] = useState(null);
    const [bulkTripsCount, setBulkTripsCount] = useState(1);
    const [isOrderSourceApp, setIsOrderSourceApp] = useState(true);
    const [orderRequestDate, setOrderRequestDate] = useState(null);
    const [orderProject, setOrderProject] = useState(null);
    const [orderTrips, setOrderTrips] = useState([_.cloneDeep(tripFormModel())]);
    const getCurrentUserRoles = useSelector(getUserRoles());
    const [isCurrentUserSuperAdmin, setIsCurrentUserSuperAdmin] = useState(false);

    const [activeTripIndex, setActiveTripIndex] = useState(0);
    const [isSubmiting, setIsSubmiting] = useState(false);

    const isUserSupplierPartner = getCurrentUserRoles?.includes("supplier_partner_account_manager")
    const history = useHistory();

    const { register, errors, handleSubmit, setValue, getValues, trigger, control } = useForm({
        mode: "onSubmit",
    });

    const [createField, setCreateField] = useState(null);
    const dispatch = useDispatch();

    async function fetchEntity(url) {
        const response = await Api.get(url);
        return response;
    }
    const contractTypeEntityOptions = isUserSupplierPartner ? { ...orderFormFields.contractType, data: supplierPartnerContractTypeData } : orderFormFields.contractType
    const contractTypeOptions = isUserSupplierPartner ? supplierPartnerContractTypeData : orderFormFields.contractType.data
    useEffect(() => {
        setIsCurrentUserSuperAdmin(
            getCurrentUserRoles.some((role) => role === userRoles.SUPERADMIN)
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //  Register form hooks for file fields or dropdowns
    useEffect(() => {
        orderFormFields.trip
            .filter((ff) => ff.type === "list" || ff.type === "file")
            .forEach((ff) => {
                register({ name: ff.name });
            });
    }, [register]);

    // Effect to handle active trip changes on Create and Delete (orderTrips.length), or click paging buttons
    useEffect(() => {
        setActiveFormValues();

        const activeTrip = orderTrips[activeTripIndex];
        if (activeTrip.isValid !== null) {
            trigger();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTripIndex, orderTrips.length]);

    useEffect(() => {
        if (orderBranch) {
            async function fetchLocation() {
                const location = await getBranchLocation(orderBranch.value);
                setBranchLocation(_.values(location.entities["locations"])[0].attributes);
            }
            fetchLocation();
        }
    }, [orderBranch]);
    // ttest update trips

    const switchForm = (i) => {
        if (i === activeTripIndex) return;

        validateActiveTrip();
        setActiveTripIndex(i);
    };

    const validateActiveTrip = () => {
        // add validation state by refrence to avoid unnecessary rerender
        const activeTrip = orderTrips[activeTripIndex];
        activeTrip.isValid = isTripValid(activeTrip);
        validateActiveTripPoints();
    };

    const validateActiveTripPoints = (activeTrip = orderTrips[activeTripIndex]) => {
        trackAnalytics("Order submission clicks");

        activeTrip.isPointsValid = isTripPointsValid(activeTrip.tripPoints, activeTrip.tripType);
    };

    const createTripForm = () => {
        validateActiveTrip();
        setOrderTrips([
            ..._.cloneDeep(orderTrips),
            _.cloneDeep(tripFormModel(orderTrips[activeTripIndex])),
        ]);
        switchForm(orderTrips.length);
    };

    const deleteTripForm = () => {
        const orderTripsClone = _.cloneDeep(orderTrips);
        orderTripsClone.splice(activeTripIndex, 1);

        setOrderTrips(orderTripsClone);

        if (activeTripIndex === orderTrips.length - 1) {
            switchForm(activeTripIndex - 1);
        }
    };

    const setActiveFormValues = () => {
        const activeTripForm = orderTrips[activeTripIndex];
        const organizationFieldName = orderFormFields.organization.name;

        orderFormFields.trip.forEach((field) => setValue(field.name, activeTripForm[field.name]));
        setValue(organizationFieldName, orderOrganization);
    };

    const saveActiveForm = (form = getValues()) => {
        // organization added to the form for validation only but the actual value saved in organization
        const { organization, requestDateAndTime, ...activeFormValues } = form;
        const allTripFormsClone = _.cloneDeep(orderTrips);

        allTripFormsClone[activeTripIndex] = {
            ..._.cloneDeep(activeFormValues),
            tripPoints: _.cloneDeep(allTripFormsClone[activeTripIndex].tripPoints),
            isPointsValid: allTripFormsClone[activeTripIndex].isPointsValid,
        };
        setOrderRequestDate(requestDateAndTime);
        setOrderTrips(allTripFormsClone);
    };

    const handleTripPointsChange = (tripPoints) => {
        const allTripFormsClone = _.cloneDeep(orderTrips);
        allTripFormsClone[activeTripIndex].tripPoints = tripPoints;

        if (allTripFormsClone[activeTripIndex].isPointsValid !== null) {
            validateActiveTripPoints(allTripFormsClone[activeTripIndex]);
        }

        setOrderTrips(allTripFormsClone);
    };
    const triggerOpertionalModel = async () => await trigger("supplierContractType");

    const handleSupplierChange = (value) => {
        setValue("supplier", value);
        setOrderSupplier(value);
        if (value !== null) {
            register("supplierContractType", { required: true });
        } else {
            register("supplierContractType", { required: false });
        }
        triggerOpertionalModel();

        // change current form state
        setValue("driver", null);
        setValue("truck", null);

        trigger("supplier"); // revalidate on change

        // Remove all trips driver/truck because the choosen values depends on the supplier name
        const newTrips = orderTrips.map((item) => ({
            ...item,
            driver: null,
            truck: null,
        }));
        setOrderTrips(newTrips);
    };

    const handleTruckChange = async (truckTypeId) => {
        if (truckTypeId === null) return;
        const truckTypeData = await fetchEntity(`/truck-types/${truckTypeId}`);
        const truckTypeEntity = Object.values(truckTypeData?.entities)[0][`_${truckTypeId}`];
        const truckTypeLabel = truckTypeEntity?.attributes?.alias;
        setValue("truckType", { value: truckTypeId, label: truckTypeLabel });
        await trigger("truckType");
        saveActiveForm();
    };

    const handleListChange = (option, name, entityData) => {
        const listValue = option[0] ? option[0] : null;
        if (name === orderFormFields.organization.name) {
            setOrderOrganization(listValue);
            setOrderBranch(null);
        } else if (name === "branch") {
            setOrderBranch(listValue);
            option.length === 0 && setBranchLocation(null);
        } else if (name === "supplier") {
            setValue("driver", null);
            setValue("truck", null);
            setOrderSupplier(listValue);
            if (listValue !== null) {
                register("supplierContractType", { required: true });
            } else {
                register("supplierContractType", { required: false });
            }
            triggerOpertionalModel();
        } else if (name === "truck") {
            setValue("truckType", null);
            const truckTypeId = entityData?.attributes?.truckTypeId;
            handleTruckChange(truckTypeId);
        } else if (name === "truckType") {
            !orderTrips[0]?.truck?.value && setValue("truck", null);
        } else if (name === orderFormFields.contractType.name) {
            setContractType(listValue);
        } else if (name === orderFormFields.project.name) {
            setOrderProject(listValue);
        }

        const value = {
            ...listValue,
            ...(entityData ? entityData.attributes : {}),
        };

        setValue(name, _.isEmpty(value) ? null : value);
        trigger(name); // revalidate on change
        saveActiveForm();
    };

    const handleDateChange = (name, date) => {
        setValue(name, date);
        trigger(name);
        saveActiveForm();
    };

    const handleFileUpload = (name, fileList) => {
        if (fileList.length === 0) {
            setValue(name, []);
            saveActiveForm();
            return;
        }

        setValue(name, fileList);
        saveActiveForm();
    };

    const handleFileRemove = (name, fileList) => {
        setValue(name, fileList);
        saveActiveForm();
    };

    const saveTextArea = () => {
        saveActiveForm();
    };

    const debounceSaveText = _.debounce(saveTextArea, 500);

    const handleTextAreaChange = (name, value) => {
        setValue(name, value);
        debounceSaveText();
    };

    const handleCheckboxChange = (name, value) => {
        if (name === "tripsSource") {
            setIsOrderSourceApp(value);
        } else {
            setValue(name, value);
            saveActiveForm();
        }
    };

    const handleSelect = ({ name, value }) => {
        setValue(name, value);
        saveActiveForm();
    };

    const handleCreateEntity = (options) => {
        setCreateField(options);
    };

    const handleCreateSubmit = (createdEntityResource) => {
        setCreateField(null);

        if (!createdEntityResource) {
            return;
        }

        const supplierField = "supplier";
        const createdEntity = extractResourceAttributes(createdEntityResource);
        const createdEntityValue = {
            label: generateEntityLabel(createdEntityResource[`_${createdEntity.id}`], createField),
            value: createdEntity.id,
        };

        if (createField.name === supplierField.name) {
            handleSupplierChange(createdEntityValue);
        } else {
            handleListChange([createdEntityValue], createField.name);
        }
    };

    const submitOrder = async (data, e) => {
        saveActiveForm();

        if (!isOrderValid(orderOrganization, orderTrips)) {
            return false;
        }

        setIsSubmiting(true);
        const orderEntity = mapOrderToEntityRequest(
            orderOrganization,
            orderBranch,
            contractType,
            isOrderSourceApp,
            orderRequestDate,
            orderProject,
            orderTrips
        );

        postOrder(orderEntity)
            .then((createdOrder) => {
                const orderTripIds = getTripIds(createdOrder);
                const redirectingDestination = orderTripIds
                    ? `/database/trips/${orderTripIds[0]}`
                    : "/database/trips";
                setIsSubmiting(false);
                trackAnalytics("Successful order submissions", {
                    contractType: contractType.label,
                    Client: orderOrganization.label,
                    Branches: orderBranch.label,
                    Source: isOrderSourceApp,
                    Platform: "Admin",
                    createdTripsPerOrder: orderTrips.length,
                    OrderProject: orderProject?.label,
                });
                history.push(redirectingDestination);
            })
            .catch((error) => {
                trackAnalytics(" Failure order submissions", {
                    contractType: contractType.label,
                    Client: orderOrganization.label,
                    Branches: orderBranch.label,
                    Source: isOrderSourceApp,
                    Platform: "Admin",
                    createdTripsPerOrder: orderTrips.length,
                    OrderProject: orderProject?.label,
                });
                dispatch(
                    setMessage({
                        content: error,
                        title: `Couldn't create order`,
                        isError: true,
                    })
                );
                setIsSubmiting(false);
            });
    };
    return (
        <S.CreateOrderWrapper isBulk={isBulk}>
            <form onSubmit={handleSubmit(submitOrder)}>
                <EuiFlexGroup
                    gutterSize="none"
                    className="create-order__header"
                >
                    <OrderColumn
                        title={<FormattedMessage id="order-form.client" />}
                        id="organization-column"
                        required
                    >
                        <AbstractDropdown
                            register={register}
                            errors={errors}
                            entityOptions={orderFormFields.organization}
                            onValueChange={handleListChange}
                            selectedOptions={orderOrganization ? [orderOrganization] : []}
                        />
                    </OrderColumn>
                    <OrderColumn
                        title={<FormattedMessage id="order-form.client-branch" />}
                        id="organization-branch-column"
                        required
                    >
                        <AbstractDropdown
                            register={register}
                            errors={errors}
                            entityOptions={{
                                ...orderFormFields.branch,
                                url:
                                    orderOrganization &&
                                    `/organizations/${orderOrganization.value}/branches`,
                                filterResource:
                                    orderOrganization &&
                                    `filter[organizationId]=${orderOrganization.value}`,
                            }}
                            onValueChange={handleListChange}
                            selectedOptions={orderBranch ? [orderBranch] : []}
                            isDisabled={!orderOrganization}
                        />
                    </OrderColumn>
                </EuiFlexGroup>

                <EuiFlexGroup
                    gutterSize="none"
                    className="create-order__header"
                >
                    <OrderColumn
                        title={<FormattedMessage id="order-form.contract-Type" />}
                        id="organization-contract-Type-column"
                        required
                    >
                        <AbstractDropdown
                            register={register}
                            errors={errors}
                            entityOptions={contractTypeEntityOptions}
                            options={contractTypeOptions}
                            onValueChange={handleListChange}
                            selectedOptions={contractType ? [contractType] : []}
                            isDisabled={!orderOrganization}
                        />
                    </OrderColumn>

                    <OrderColumn
                        title={<FormattedMessage id="order-form.request-date" />}
                        required
                    >
                        <AbstractDate
                            options={orderFormFields.requestDateAndTime}
                            errors={errors}
                            onDateChange={(date) =>
                                handleDateChange(orderFormFields.requestDateAndTime.name, date)
                            }
                            register={register}
                            control={control}
                        />
                    </OrderColumn>
                </EuiFlexGroup>
                {contractType?.label === "Monthly" && (
                    <EuiFlexGroup
                        gutterSize="none"
                        className="create-order__header"
                    >
                        <OrderColumn
                            title={<FormattedMessage id="order-form.project" />}
                            id="organization-project-column"
                            required={!isUserSupplierPartner}
                        >
                            <AbstractDropdown
                                register={register}
                                errors={errors}
                                entityOptions={{
                                    ...orderFormFields.project,
                                    url:
                                        orderOrganization && isCurrentUserSuperAdmin
                                            ? `/projects?filter[organization_id]=${orderOrganization?.value}`
                                            : `/projects?filter[organization_id]=${orderOrganization?.value}&filter[status]=approved`,
                                    filterResource:
                                        orderOrganization && isCurrentUserSuperAdmin
                                            ? `filter[organization_id]=${orderOrganization?.value}`
                                            : `filter[organization_id]=${orderOrganization?.value}&filter[status]=approved`,
                                    validation: {
                                        required: !isUserSupplierPartner
                                    }
                                }}
                                onValueChange={handleListChange}
                                selectedOptions={orderProject ? [orderProject] : []}
                                isDisabled={!orderOrganization}
                            />
                        </OrderColumn>
                        <OrderColumn />
                    </EuiFlexGroup>
                )}
                <EuiFlexGroup
                    gutterSize="none"
                    className="create-order__header"
                >
                    <OrderColumn
                        title={<FormattedMessage id="order-form.source" />}
                        required
                    >
                        <EuiCheckbox
                            id="tripSource"
                            label={<FormattedMessage id="order-form.source-expatiation" />}
                            checked={isOrderSourceApp}
                            onChange={(e) => handleCheckboxChange(e.target.name, e.target.checked)}
                            name="tripsSource"
                            inputRef={register}
                        />
                    </OrderColumn>
                </EuiFlexGroup>

                {isBulk && (
                    <OrderColumn
                        title={<FormattedMessage id="order-form.trucks-number" />}
                        id="bulk-trip-field"
                        required
                    >
                        <AbstractNumber
                            options={orderFormFields.tripsCount}
                            errors={errors}
                            onChange={(number) => setBulkTripsCount(number)}
                            selectedValue={bulkTripsCount}
                        />
                    </OrderColumn>
                )}

                <S.CreateTripContainer>
                    {isBulk ? (
                        <div className="create-order__bulk-counter">
                            <span>
                                <small>X</small>
                                <b> {bulkTripsCount}</b>
                            </span>
                        </div>
                    ) : (
                        <>
                            <div className="create-order__create-trip-btn-wrapper">
                                <EuiButtonIcon
                                    onClick={createTripForm}
                                    size="m"
                                    aria-label="add trip"
                                    iconSize="xxl"
                                    color="success"
                                    title={<FormattedMessage id="order-form.add-trip" />}
                                    iconType="plusInCircleFilled"
                                />
                            </div>
                            <EuiPagination
                                className="create-order__trips-pagination"
                                aria-label="Trips pagination"
                                pageCount={orderTrips.length}
                                activePage={activeTripIndex}
                                onPageClick={(activePage) => switchForm(activePage)}
                            />
                        </>
                    )}
                    {orderTrips.length > 1 && (
                        <div className="create-order__delete-trip-btn-wrapper">
                            <EuiButtonIcon
                                onClick={deleteTripForm}
                                aria-label="Delete trip"
                                iconSize="m"
                                color="danger"
                                title={<FormattedMessage id="order-form.remove-trip" />}
                                iconType="trash"
                            />
                        </div>
                    )}
                    <TripForm
                        formValues={orderTrips[activeTripIndex]}
                        tripFormFields={orderFormFields.trip}
                        supplier={orderSupplier}
                        organizationId={orderOrganization?.value}
                        isManual={!isOrderSourceApp}
                        register={register}
                        control={control}
                        errors={errors}
                        onListChange={handleListChange}
                        onDateChange={handleDateChange}
                        onTripPointsChange={handleTripPointsChange}
                        onTextAreaChange={handleTextAreaChange}
                        onCheckboxChange={handleCheckboxChange}
                        onFileUpload={handleFileUpload}
                        onFileRemove={handleFileRemove}
                        onCreateEntity={handleCreateEntity}
                        onSelectChange={handleSelect}
                        branchLocation={branchLocation}
                    // opertionalModelIsRequired={opertionalModelIsRequired}
                    />
                </S.CreateTripContainer>
                <EuiTextAlign
                    className="submit-btn-wrapper"
                    textAlign="center"
                >
                    <EuiButton
                        className="create-order__submit-btn"
                        type="submit"
                        size="m"
                        color="secondary"
                        isLoading={isSubmiting}
                        onClick={(e) => validateActiveTripPoints()}
                        fill
                    >
                        <FormattedMessage id="order-form.submit-btn" />
                    </EuiButton>
                </EuiTextAlign>
            </form>

            {createField && (
                <AbstractFormContainer
                    options={createField.createOptions}
                    onFinish={handleCreateSubmit}
                    includedEntities={{}}
                    entityObject={
                        createField.createOptions.relation &&
                            createField.createOptions.relation.name === "supplier"
                            ? {
                                supplierId: orderSupplier.value,
                                supplier: orderSupplier.label,
                            }
                            : {}
                    }
                />
            )}
        </S.CreateOrderWrapper>
    );
}
